import { useAuth0 } from '@auth0/auth0-react'
import {
  TemplateType,
  useProductPoolsLazyQuery,
  useProviderPoolsLazyQuery,
  useTaxonomyItemPoolsLazyQuery,
} from '@generated/graphql'
import {
  setProductPools,
  setProductPoolsLoading,
  setProviderPools,
  setProviderPoolsLoading,
  setTaxonomyItemPools,
  setTaxonomyItemPoolsLoading,
} from '@store/slices/pools'
import { useAppDispatch } from '@store/typedHooks'
import { useCallback, useEffect } from 'react'

export function useFetchPools({ refetchOnly = false, type }) {
  const dispatch = useAppDispatch()
  const { isAuthenticated } = useAuth0()

  const [
    queryProviderPools,
    { refetch: refetchProviderPoolsQuery, called: providerPoolsCalled },
  ] = useProviderPoolsLazyQuery({
    notifyOnNetworkStatusChange: true,
    // fetchPolicy: 'cache-and-network',
    fetchPolicy: 'network-only',
    onCompleted: ({ providerPools }) => {
      dispatch(setProviderPools(providerPools))
    },
    onError: error => {
      console.error('Unable to load pools', error)
    },
  })

  const [
    queryTaxonomyItemPools,
    { refetch: refetchTaxonomyItemPoolsQuery, called: taxonomyItemPoolsCalled },
  ] = useTaxonomyItemPoolsLazyQuery({
    notifyOnNetworkStatusChange: true,
    // fetchPolicy: 'cache-and-network',
    fetchPolicy: 'network-only',
    onCompleted: ({ taxonomyItemPools }) => {
      dispatch(setTaxonomyItemPools(taxonomyItemPools))
    },
    onError: error => {
      console.error('Unable to load pools', error)
    },
  })

  const [
    queryProductPools,
    { refetch: refetchProductPoolsQuery, called: productPoolsCalled },
  ] = useProductPoolsLazyQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    onCompleted: ({ productPools }) => {
      dispatch(setProductPools(productPools))
    },
    onError: error => {
      console.error('Unable to load pools', error)
    },
  })

  useEffect(() => {
    if (!isAuthenticated || refetchOnly || type !== TemplateType.Provider) {
      return
    }

    dispatch(setProviderPoolsLoading(true))
    queryProviderPools()
  }, [isAuthenticated, queryProviderPools, refetchOnly, dispatch, type])

  useEffect(() => {
    if (!isAuthenticated || refetchOnly || type !== TemplateType.Taxonomy) {
      return
    }

    dispatch(setTaxonomyItemPoolsLoading(true))
    queryTaxonomyItemPools()
  }, [isAuthenticated, queryTaxonomyItemPools, refetchOnly, dispatch, type])

  useEffect(() => {
    if (!isAuthenticated || refetchOnly || type !== TemplateType.Product) {
      return
    }

    dispatch(setProductPoolsLoading(true))
    queryProductPools()
  }, [isAuthenticated, queryProductPools, refetchOnly, dispatch, type])

  const refetchProviderPools = useCallback(async () => {
    dispatch(setProviderPoolsLoading(true))
    if (providerPoolsCalled) {
      await refetchProviderPoolsQuery()
    } else {
      await queryProviderPools()
    }
  }, [dispatch, refetchProviderPoolsQuery, providerPoolsCalled])

  const refetchTaxonomyItemPools = useCallback(async () => {
    dispatch(setTaxonomyItemPoolsLoading(true))
    if (taxonomyItemPoolsCalled) {
      await refetchTaxonomyItemPoolsQuery()
    } else {
      await queryTaxonomyItemPools()
    }
  }, [dispatch, refetchTaxonomyItemPoolsQuery, taxonomyItemPoolsCalled])

  const refetchProductPools = useCallback(async () => {
    dispatch(setProductPoolsLoading(true))
    if (productPoolsCalled) {
      await refetchProductPoolsQuery()
    } else {
      await queryProductPools()
    }
  }, [dispatch, refetchProductPoolsQuery, productPoolsCalled])

  return { refetchProviderPools, refetchTaxonomyItemPools, refetchProductPools }
}
